import React, { useState } from 'react';
import { Alert, Button, CopyButton, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFeedToken, useRotateFeedToken } from '../../hooks/useCalendarApi';

/** Ссылку на ленту показываем один раз: в базе лежит только её хэш. */
export const SubscribeModal: React.FC<{ opened: boolean; onClose: () => void }> = ({
  opened,
  onClose,
}) => {
  const { t } = useTranslation();
  const token = useFeedToken();
  const rotate = useRotateFeedToken();
  const [url, setUrl] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Ошибку показывает `rotate.isError` ниже — без него кнопка молча гасла.
  const issue = () =>
    rotate.mutate(undefined, {
      onSuccess: (result) => {
        setUrl(result.url);
        setIsConfirming(false);
      },
    });

  const close = () => {
    rotate.reset();
    setUrl(null);
    setIsConfirming(false);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={close} title={t('calendar.subscribe.title')} centered>
      <Stack>
        <Text size="sm">{t('calendar.subscribe.intro')}</Text>

        {url ? (
          <>
            <TextInput value={url} readOnly />
            <CopyButton value={url}>
              {({ copied, copy }) => (
                <Button onClick={copy}>
                  {copied ? t('calendar.subscribe.copied') : t('calendar.subscribe.copy')}
                </Button>
              )}
            </CopyButton>
            <Alert color="yellow">{t('calendar.subscribe.secret')}</Alert>
          </>
        ) : token.data?.hasToken ? (
          <Stack gap="xs">
            <Text size="sm">
              {isConfirming
                ? t('calendar.subscribe.rotateConfirm')
                : t('calendar.subscribe.issued')}
            </Text>
            <Group>
              <Button
                variant={isConfirming ? 'filled' : 'light'}
                color={isConfirming ? 'red' : undefined}
                loading={rotate.isPending}
                onClick={() => (isConfirming ? issue() : setIsConfirming(true))}
              >
                {t('calendar.subscribe.rotate')}
              </Button>
            </Group>
          </Stack>
        ) : (
          <Button loading={rotate.isPending} onClick={issue}>
            {t('calendar.subscribe.create')}
          </Button>
        )}

        {rotate.isError && <Alert color="red">{t('calendar.subscribe.failed')}</Alert>}

        <Text size="xs">{t('calendar.subscribe.google')}</Text>
        <Text size="xs">{t('calendar.subscribe.outlook')}</Text>
        <Text size="xs">{t('calendar.subscribe.apple')}</Text>
        <Text size="xs" c="dimmed">
          {t('calendar.subscribe.delay')}
        </Text>
      </Stack>
    </Modal>
  );
};
